/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { Typography2, breakpoints } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import KeyInformationDetailsMobileItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDetailsMobileItem';
import type { KIM_MOBILE_SUBTITLE_DISPLAY_OPTIONS } from 'bundles/unified-description-page-common/constants';
import { useStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

type Props = {
  title?: React.ReactNode | null;
  subtitle?: React.ReactNode;
  numSubtitleLines?: number;
  mobileSubtitleDisplayOption?: (typeof KIM_MOBILE_SUBTITLE_DISPLAY_OPTIONS)[keyof typeof KIM_MOBILE_SUBTITLE_DISPLAY_OPTIONS];
  modalTitle?: React.ReactNode;
};

const styles = {
  subtitle: (numSubtitleLines: number) => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: ${numSubtitleLines};
    -webkit-box-orient: vertical;
  `,
};

const KeyInformationDetailsItem = ({
  title,
  subtitle,
  numSubtitleLines,
  mobileSubtitleDisplayOption,
  modalTitle,
}: Props) => {
  const userAgent = useUserAgent();
  const isStreamlinedXDP = useStreamlinedXDP();

  if (!title) return null;

  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) => (
        <React.Fragment>
          {matches && isStreamlinedXDP && subtitle ? (
            <KeyInformationDetailsMobileItem
              title={title}
              subtitle={subtitle}
              subtitleDisplayOption={mobileSubtitleDisplayOption}
              modalTitle={modalTitle}
            />
          ) : (
            <div>
              <Typography2 variant="subtitleLarge" component="div">
                {title}
              </Typography2>
              <Typography2
                variant="bodySecondary"
                component="div"
                color="supportText"
                css={numSubtitleLines && styles.subtitle(numSubtitleLines)}
              >
                {subtitle}
              </Typography2>
            </div>
          )}
        </React.Fragment>
      )}
    </Media>
  );
};

export default KeyInformationDetailsItem;
